import { useEffect, useMemo, useRef, useState } from "react";
import { trackTicket, submitTicket } from "../api/serviceDeskApi";
import { submitSaleInquiryDb } from "../api/cmsApi";
import { CONTACT_PHONE, CONTACT_PHONE_TEL, WHATSAPP_URL } from "../data/constants";

const KNOWLEDGE = {
  greetings: ["hi", "hello", "hey", "good morning", "good afternoon", "good evening", "namaste", "hii"],
  server: ["server", "rack server", "tower server", "blade server", "poweredge", "proliant", "hpe", "dell", "supermicro", "processor", "cpu", "ram", "memory", "bios", "ipmi", "idrac", "ilo", "esxi", "vmware", "hypervisor", "virtualization", "load", "overheat", "overheating", "boot", "reboot", "not starting", "down", "crash", "performance"],
  switch: ["switch", "router", "cisco", "juniper", "aruba", "vlan", "trunk", "port", "sfp", "uplink", "bandwidth", "latency", "packet", "network", "connectivity", "lan", "wan", "firewall", "poe", "managed", "unmanaged"],
  storage: ["storage", "nas", "san", "raid", "disk", "drive", "ssd", "hdd", "backup", "data loss", "array", "synology", "netapp", "emc"],
  cloud: ["cloud", "aws", "azure", "google cloud", "hybrid", "migration", "saas", "iaas", "paas", "hosting"],
  amc: ["amc", "annual maintenance", "contract", "warranty", "support plan", "renewal", "onsite", "remote support", "sla"],
  pricing: ["price", "cost", "quote", "pricing", "how much", "charges", "invoice", "purchase", "buy", "sales", "sale inquiry", "enquiry", "get a quote", "contact sales"],
  ticket: ["ticket", "complaint", "issue", "problem", "not resolved", "raise", "lodge", "escalate", "help", "support", "service desk"],
};

const RESPONSES = {
  greeting: {
    tag: "WELCOME",
    text: "Hello! Welcome to Emicron Tech Solutions support. I can help with servers, networking, storage, cloud, AMC, sale inquiries, and service desk tickets. How can I assist you today?",
    quickReplies: ["Server issue", "Switch help", "Cloud query", "Sale inquiry", "Raise a ticket"],
  },
  server: {
    tag: "SERVERS",
    text: "I can help with server queries: Dell PowerEdge, HPE ProLiant, SuperMicro, boot/performance issues, iDRAC/iLO, and virtualization (VMware/Hyper-V). Please share your specific issue.",
    quickReplies: ["Server not booting", "Performance issue", "Hardware fault", "Raise a ticket"],
  },
  switch: {
    tag: "NETWORKING",
    text: "For switching and networking support: Cisco, Juniper, Aruba, VLANs, trunking, port issues, and latency/connectivity troubleshooting. Please share switch model and issue.",
    quickReplies: ["VLAN setup", "Port not working", "Network slow", "Raise a ticket"],
  },
  storage: {
    tag: "STORAGE",
    text: "I can help with NAS/SAN/RAID, disk failures, backup, and data recovery guidance. Share what happened and which device you are using.",
    quickReplies: ["Disk failure", "RAID issue", "Data recovery", "Raise a ticket"],
  },
  cloud: {
    tag: "CLOUD SERVICES",
    text: "Emicron supports AWS, Azure, GCP, hybrid cloud, migration, and managed services. Tell me your requirement and current setup.",
    quickReplies: ["Cloud migration", "Managed services", "Hosting query", "Raise a ticket"],
  },
  amc: {
    tag: "AMC & SUPPORT",
    text: "We offer AMC plans with SLA options, remote helpdesk, and onsite support. Share your requirement to proceed.",
    quickReplies: ["AMC renewal", "SLA details", "Get a quote", "Raise a ticket"],
  },
  pricing: {
    tag: "PRICING",
    text: "Pricing depends on configuration and SLA. I can help raise a sales enquiry for a quick quote.",
    quickReplies: ["Get a quote", "Sale inquiry", "Raise a ticket"],
  },
  fallback: {
    tag: "NEED HELP?",
    text: "I can assist with servers, networking, storage, cloud, AMC, sale inquiries, and ticket raising. Please share your exact requirement.",
    quickReplies: ["Server help", "Sale inquiry", "Raise a ticket", "Talk to agent"],
  },
};

const PRODUCT_CATALOG = [
  {
    category: "server",
    model: "Dell PowerEdge R550 (2U)",
    formFactor: "2U",
    cpu: "Intel Xeon Silver 4310",
    ram: "64 GB DDR4",
    storage: "2 x 1.2TB SAS",
    price: 285000,
  },
  {
    category: "server",
    model: "HPE ProLiant DL380 Gen10 Plus (2U)",
    formFactor: "2U",
    cpu: "Intel Xeon Silver 4314",
    ram: "64 GB DDR4",
    storage: "2 x 960GB SSD",
    price: 298000,
  },
  {
    category: "server",
    model: "Lenovo ThinkSystem SR650 V2 (2U)",
    formFactor: "2U",
    cpu: "Intel Xeon Silver 4310",
    ram: "64 GB DDR4",
    storage: "2 x 960GB SSD",
    price: 276000,
  },
  {
    category: "server",
    model: "Dell PowerEdge R650xs (1U)",
    formFactor: "1U",
    cpu: "Intel Xeon Silver 4310",
    ram: "32 GB DDR4",
    storage: "2 x 600GB SAS",
    price: 242000,
  },
  {
    category: "switch",
    model: "Cisco Catalyst 9200L 24-port",
    formFactor: "1U",
    cpu: "Managed L3",
    ram: "4 GB",
    storage: "128 GB flash",
    price: 185000,
  },
  {
    category: "switch",
    model: "Juniper EX3400 24T",
    formFactor: "1U",
    cpu: "Managed L3",
    ram: "4 GB",
    storage: "16 GB flash",
    price: 212000,
  },
  {
    category: "storage",
    model: "Dell EMC Unity XT 380 (entry)",
    formFactor: "2U",
    cpu: "Dual controller",
    ram: "32 GB cache",
    storage: "7.68 TB usable",
    price: 325000,
  },
  {
    category: "storage",
    model: "NetApp FAS2720",
    formFactor: "2U",
    cpu: "Dual controller",
    ram: "32 GB cache",
    storage: "9.6 TB usable",
    price: 342000,
  },
];

const TROUBLESHOOTING_TREES = [
  {
    id: "server-not-booting",
    title: "Server not booting",
    category: "Servers",
    triggers: ["server not booting", "server not starting", "not booting", "no boot", "server down", "poweredge not booting", "proliant not booting"],
    steps: [
      "Check power cables, PSU LEDs, and PDU power status.",
      "Disconnect/reconnect power and perform a cold reboot.",
      "Check monitor/KVM output for BIOS POST or error code.",
      "Enter BIOS/iDRAC/iLO logs and verify hardware alerts.",
      "Check RAID controller status and disk health.",
      "Boot with minimum hardware profile (single CPU/RAM, no extra PCI cards).",
    ],
  },
  {
    id: "network-port-down",
    title: "Network port not working",
    category: "Switching & Networking",
    triggers: ["port not working", "switch port down", "network port down", "no link light", "lan not working"],
    steps: [
      "Check cable seating and link LEDs on both switch and endpoint.",
      "Swap patch cable and test with a known-good cable.",
      "Verify interface admin status (not shutdown) on switch.",
      "Check VLAN assignment and trunk/access configuration.",
      "Review port-security/ACL logs for blocked MAC or policy deny.",
      "Test endpoint NIC on another known-good port.",
    ],
  },
  {
    id: "raid-degraded",
    title: "RAID degraded / disk failure",
    category: "Storage",
    triggers: ["raid degraded", "raid issue", "disk failed", "array failed", "storage degraded"],
    steps: [
      "Identify failed disk from RAID controller/array manager.",
      "Confirm current RAID health and backup availability.",
      "Replace failed disk with same/supported capacity and type.",
      "Start rebuild and monitor rebuild percentage.",
      "Check controller/event logs for medium or parity errors.",
      "Run consistency check after rebuild completes.",
    ],
  },
];

const MOCK_AMC_CONTRACTS = [
  {
    key: "AMC-EMI-2024-1182",
    customer: "Acme Manufacturing Pvt Ltd",
    plan: "Comprehensive AMC",
    startDate: "2024-04-01",
    expiryDate: "2025-03-31",
    status: "Expired",
    assets: "12 servers, 22 switches",
    sla: "8 business hours",
  },
  {
    key: "AMC-EMI-2025-0744",
    customer: "Northstar Logistics",
    plan: "Standard AMC",
    startDate: "2025-07-01",
    expiryDate: "2026-06-30",
    status: "Active",
    assets: "8 servers, 14 switches",
    sla: "Next business day",
  },
  {
    key: "support@northstarlogistics.in",
    customer: "Northstar Logistics",
    plan: "Standard AMC",
    startDate: "2025-07-01",
    expiryDate: "2026-06-30",
    status: "Active",
    assets: "8 servers, 14 switches",
    sla: "Next business day",
  },
];

function nowTime() {
  return new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function formatInr(value) {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(value);
  } catch (_) {
    return "INR " + String(value);
  }
}

function parseBudgetRupees(text) {
  const lakh = text.match(/(?:under|below|upto|up to)?\s*₹?\s*([\d.]+)\s*(lakh|lakhs|lac|lacs)\b/i);
  if (lakh) {
    return Math.round(parseFloat(lakh[1]) * 100000);
  }
  const rupees = text.match(/(?:under|below|upto|up to)?\s*₹\s*([\d,]+)/i) || text.match(/(?:under|below|upto|up to)\s*([\d,]{4,})\b/i);
  if (rupees) {
    return parseInt(String(rupees[1]).replace(/,/g, ""), 10);
  }
  return null;
}

function parseFormFactor(text) {
  const m = text.match(/\b([124])\s*u\b/i);
  return m ? m[1] + "U" : null;
}

function parseCatalogCategory(text) {
  const t = text.toLowerCase();
  if (/\b(server|rack server|tower server|blade)\b/.test(t)) return "server";
  if (/\b(switch|switches|router|network)\b/.test(t)) return "switch";
  if (/\b(storage|san|nas|array)\b/.test(t)) return "storage";
  return null;
}

function getTroubleshootingTree(query) {
  const t = query.toLowerCase();
  return TROUBLESHOOTING_TREES.find((tree) => tree.triggers.some((k) => t.includes(k))) || null;
}

function isAmcRenewalQuery(text) {
  const t = text.toLowerCase();
  return /(amc|annual maintenance|contract).*(expire|expiry|renew|renewal|status)/.test(t)
    || /(when).*(amc|contract).*(expire|expiry)/.test(t)
    || /(amc renewal|contract renewal)/.test(t);
}

function lookupAmcContract(query) {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const exact = MOCK_AMC_CONTRACTS.find((c) => c.key.toLowerCase() === q);
  if (exact) return exact;
  if (q.includes("@")) {
    return MOCK_AMC_CONTRACTS.find((c) => c.key.includes("@") && c.key.toLowerCase() === q) || null;
  }
  return null;
}

function getProductSuggestion(query) {
  const q = query.trim();
  const category = parseCatalogCategory(q);
  const budgetRupees = parseBudgetRupees(q);
  const formFactor = parseFormFactor(q);
  const catalogIntent = /\b(need|looking|recommend|suggest|compare|spec|configuration|under|budget|cost)\b/i.test(q);
  if (!category || (!catalogIntent && budgetRupees === null)) return null;

  let pool = PRODUCT_CATALOG.filter((p) => p.category === category);
  if (formFactor) {
    const ff = pool.filter((p) => p.formFactor.toLowerCase() === formFactor.toLowerCase());
    if (ff.length) pool = ff;
  }
  pool = [...pool].sort((a, b) => a.price - b.price);

  let selected = pool;
  let overBudget = false;
  if (budgetRupees !== null) {
    const within = pool.filter((p) => p.price <= budgetRupees);
    if (within.length) {
      selected = within;
    } else {
      overBudget = true;
    }
  }
  selected = selected.slice(0, 3);
  if (!selected.length) return null;

  return {
    category,
    budgetRupees,
    formFactor,
    overBudget,
    items: selected,
  };
}

function formatStatusLabel(status) {
  const map = {
    open: "Open",
    in_progress: "In Progress",
    resolved: "Resolved",
    closed: "Closed",
  };
  return map[status] || status || "Unknown";
}

function isSaleInquiryPrompt(text) {
  const t = String(text || "").toLowerCase().trim();
  return (
    t === "sale inquiry" ||
    t === "sales inquiry" ||
    t === "get a quote" ||
    t === "get exact quote" ||
    t === "contact sales" ||
    t.includes("sale inquiry") ||
    t.includes("sales enquiry") ||
    t.includes("sales inquiry")
  );
}

function classifyIntent(text) {
  const t = text.toLowerCase();
  if (KNOWLEDGE.greetings.some((w) => t.includes(w) || t === w)) return "greeting";
  if (isSaleInquiryPrompt(t) || KNOWLEDGE.pricing.some((w) => t.includes(w))) return "sales";
  if (KNOWLEDGE.ticket.some((w) => t.includes(w))) return "ticket";
  if (KNOWLEDGE.server.some((w) => t.includes(w))) return "server";
  if (KNOWLEDGE.switch.some((w) => t.includes(w))) return "switch";
  if (KNOWLEDGE.storage.some((w) => t.includes(w))) return "storage";
  if (KNOWLEDGE.cloud.some((w) => t.includes(w))) return "cloud";
  if (KNOWLEDGE.amc.some((w) => t.includes(w))) return "amc";
  return "fallback";
}

function buildGreetingState() {
  return [
    { id: "divider-1", type: "divider", text: "Session started - " + nowTime() },
    { id: "bot-1", from: "bot", tag: RESPONSES.greeting.tag, text: RESPONSES.greeting.text, quickReplies: RESPONSES.greeting.quickReplies },
  ];
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => buildGreetingState());
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  const [catActive, setCatActive] = useState("Servers");
  const [ticketMode, setTicketMode] = useState(false);
  const [amcMode, setAmcMode] = useState(false);
  const [unansweredFollowUps, setUnansweredFollowUps] = useState(0);
  const [callbackForms, setCallbackForms] = useState({});
  const [complaintForms, setComplaintForms] = useState({});
  const [saleForms, setSaleForms] = useState({});
  const [troubleshootProgress, setTroubleshootProgress] = useState({});
  const [intakeMode, setIntakeMode] = useState(null); // null | "server" | "cloud"
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages, typing, open]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const categories = useMemo(
    () => [
      { label: "Servers", prompt: "Servers" },
      { label: "Switches", prompt: "Switches & Networking" },
      { label: "Storage", prompt: "Storage Solutions" },
      { label: "Cloud", prompt: "Cloud Services" },
      { label: "Tickets", prompt: "Raise a Ticket" },
      { label: "Sale Inquiry", prompt: "Sale Inquiry" },
      { label: "AMC", prompt: "AMC & Support Plans" },
    ],
    []
  );

  const createEscalationMessage = () => ({
    id: "bot-escalation-" + Date.now(),
    from: "bot",
    tag: "ESCALATION TIER 2",
    escalation: true,
    text: "I may not be resolving this quickly. You can request a callback, or connect directly with our live team on WhatsApp/email.",
  });

  const registerUnansweredFollowUp = () => {
    setUnansweredFollowUps((prev) => {
      const next = prev + 1;
      if (next >= 2) {
        setMessages((m) => [...m, createEscalationMessage()]);
        return 0;
      }
      return next;
    });
  };

  const resetUnansweredFollowUp = () => {
    setUnansweredFollowUps(0);
  };

  const postSwitchContactCard = () => {
    setMessages((prev) => [
      ...prev,
      {
        id: "bot-switch-contact-" + Date.now(),
        from: "bot",
        tag: "SWITCH HELP",
        switchHelp: true,
        text: "For faster switch troubleshooting, contact our networking engineer directly:",
      },
    ]);
    resetUnansweredFollowUp();
  };

  const startServerIntake = () => {
    setTicketMode(false);
    setAmcMode(false);
    setIntakeMode("server");
    setMessages((prev) => [
      ...prev,
      {
        id: "bot-server-intake-" + Date.now(),
        from: "bot",
        tag: "SERVER ISSUE INTAKE",
        text:
          "Please share these details in one message:\n" +
          "1) Server model (e.g. DL380 / R550)\n" +
          "2) What happens on power-on (no display / POST code / beep)\n" +
          "3) Any LED/error code or iDRAC/iLO alert\n" +
          "4) Recent change before issue (power, patch, hardware, update)",
        quickReplies: [],
      },
    ]);
  };

  const startCloudIntake = () => {
    setTicketMode(false);
    setAmcMode(false);
    setIntakeMode("cloud");
    setMessages((prev) => [
      ...prev,
      {
        id: "bot-cloud-intake-" + Date.now(),
        from: "bot",
        tag: "CLOUD QUERY INTAKE",
        text:
          "Please share:\n" +
          "1) Workload type (app/db/VDI/backup)\n" +
          "2) Current environment (on-prem/AWS/Azure)\n" +
          "3) Approx users/data size\n" +
          "4) Budget or timeline",
        quickReplies: [],
      },
    ]);
  };

  const handleQuickReplyAction = (q) => {
    const normalized = String(q || "").trim().toLowerCase();
    if (normalized === "server issue") {
      setMessages((prev) => [...prev, { id: "user-" + Date.now(), from: "user", text: q }]);
      startServerIntake();
      return;
    }
    if (normalized === "switch help") {
      setMessages((prev) => [...prev, { id: "user-" + Date.now(), from: "user", text: q }]);
      postSwitchContactCard();
      return;
    }
    if (normalized === "cloud query") {
      setMessages((prev) => [...prev, { id: "user-" + Date.now(), from: "user", text: q }]);
      startCloudIntake();
      return;
    }
    if (normalized === "check ticket status" || normalized === "check another ticket") {
      setTicketMode(true);
      setAmcMode(false);
      setIntakeMode(null);
      setMessages((prev) => [
        ...prev,
        { id: "user-" + Date.now(), from: "user", text: q },
        {
          id: "bot-" + Date.now(),
          from: "bot",
          tag: "TICKET TRACKER",
          text: "Please enter your ticket number (EMI-YYYYMMDD-XXXXX).",
          quickReplies: [],
        },
      ]);
      return;
    }
    if (normalized === "guided troubleshooting") {
      sendToBot("server not booting");
      return;
    }
    if (
      normalized === "sale inquiry" ||
      normalized === "sales inquiry" ||
      normalized === "get a quote" ||
      normalized === "get exact quote" ||
      normalized === "contact sales"
    ) {
      openSaleInquiryForm(q);
      return;
    }
    sendToBot(q);
  };

  const updateComplaintField = (messageId, field, value) => {
    setComplaintForms((prev) => ({
      ...prev,
      [messageId]: {
        fullName: "",
        email: "",
        company: "",
        phone: "",
        issueType: "Other",
        issueTitle: "",
        issueDescription: "",
        priority: "Medium",
        submitting: false,
        submitted: false,
        ticketId: "",
        error: "",
        ...(prev[messageId] || {}),
        [field]: value,
      },
    }));
  };

  const submitComplaintFromChat = async (messageId) => {
    const form = {
      fullName: "",
      email: "",
      company: "",
      phone: "",
      issueType: "Other",
      issueTitle: "",
      issueDescription: "",
      priority: "Medium",
      submitting: false,
      submitted: false,
      ticketId: "",
      error: "",
      ...(complaintForms[messageId] || {}),
    };

    if (!form.fullName.trim() || !form.email.trim() || !form.company.trim() || !form.issueTitle.trim() || !form.issueDescription.trim()) {
      setComplaintForms((prev) => ({
        ...prev,
        [messageId]: { ...form, error: "Please fill all required fields.", submitting: false },
      }));
      return;
    }

    setComplaintForms((prev) => ({
      ...prev,
      [messageId]: { ...form, submitting: true, error: "" },
    }));

    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        phone: form.phone.trim() || undefined,
        issueType: form.issueType || "Other",
        issueTitle: form.issueTitle.trim(),
        issueDescription: form.issueDescription.trim(),
        priority: form.priority || "Medium",
        routedTeam: "General Support Team",
        routingPriority: "Normal",
        effectivePriority: form.priority || "Medium",
      };
      const data = await submitTicket(payload);
      const ticketId = data?.ticket?.id || "";
      setComplaintForms((prev) => ({
        ...prev,
        [messageId]: { ...form, submitting: false, submitted: true, ticketId, error: "" },
      }));
      setMessages((prev) => [
        ...prev,
        {
          id: "bot-complaint-ok-" + Date.now(),
          from: "bot",
          tag: "TICKET RAISED",
          text: `Your complaint has been submitted successfully.${ticketId ? ` Ticket ID: ${ticketId}` : ""}`,
          quickReplies: ["Check ticket status"],
        },
      ]);
      resetUnansweredFollowUp();
    } catch (err) {
      setComplaintForms((prev) => ({
        ...prev,
        [messageId]: { ...form, submitting: false, error: err?.message || "Could not submit complaint. Please try again." },
      }));
    }
  };

  const openSaleInquiryForm = (userPrompt = "Sale Inquiry") => {
    setTicketMode(false);
    setAmcMode(false);
    setIntakeMode(null);
    const messageId = "bot-sale-form-" + Date.now();
    setMessages((prev) => [
      ...prev,
      { id: "user-" + Date.now(), from: "user", text: userPrompt },
      {
        id: messageId,
        from: "bot",
        tag: "SALE INQUIRY",
        saleForm: true,
        text: "Share your details below. Our sales team will contact you shortly.",
        quickReplies: [],
      },
    ]);
    resetUnansweredFollowUp();
  };

  const updateSaleField = (messageId, field, value) => {
    setSaleForms((prev) => ({
      ...prev,
      [messageId]: {
        name: "",
        email: "",
        company: "",
        phone: "",
        interest: "Servers",
        message: "",
        submitting: false,
        submitted: false,
        inquiryId: "",
        error: "",
        ...(prev[messageId] || {}),
        [field]: value,
      },
    }));
  };

  const submitSaleFromChat = async (messageId) => {
    const form = {
      name: "",
      email: "",
      company: "",
      phone: "",
      interest: "Servers",
      message: "",
      submitting: false,
      submitted: false,
      inquiryId: "",
      error: "",
      ...(saleForms[messageId] || {}),
    };

    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.message.trim()) {
      setSaleForms((prev) => ({
        ...prev,
        [messageId]: { ...form, error: "Please fill name, email, company name, and message.", submitting: false },
      }));
      return;
    }

    setSaleForms((prev) => ({
      ...prev,
      [messageId]: { ...form, submitting: true, error: "" },
    }));

    try {
      const saved = await submitSaleInquiryDb({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        interest: form.interest || "Other",
        message: form.message.trim(),
        source: "chat",
      });
      const inquiryId = saved.id || "";
      setSaleForms((prev) => ({
        ...prev,
        [messageId]: { ...form, submitting: false, submitted: true, inquiryId, error: "" },
      }));
      setMessages((prev) => [
        ...prev,
        {
          id: "bot-sale-ok-" + Date.now(),
          from: "bot",
          tag: "SALES TEAM",
          text: `Thanks${form.company ? `, ${form.company}` : ""}! Your sale inquiry is submitted. Our sales team will reach out soon.`,
          quickReplies: ["Raise a ticket", "Talk to agent"],
        },
      ]);
      resetUnansweredFollowUp();
    } catch (err) {
      setSaleForms((prev) => ({
        ...prev,
        [messageId]: { ...form, submitting: false, error: err?.message || "Could not submit inquiry. Please try again." },
      }));
    }
  };

  const setTroubleshootStep = (messageId, step) => {
    setTroubleshootProgress((prev) => ({ ...prev, [messageId]: step }));
  };

  const updateCallbackField = (messageId, field, value) => {
    setCallbackForms((prev) => ({
      ...prev,
      [messageId]: {
        name: "",
        phone: "",
        time: "",
        submitted: false,
        error: "",
        ...(prev[messageId] || {}),
        [field]: value,
      },
    }));
  };

  const submitCallbackRequest = (messageId) => {
    const form = {
      name: "",
      phone: "",
      time: "",
      submitted: false,
      error: "",
      ...(callbackForms[messageId] || {}),
    };
    if (!form.name.trim() || !form.phone.trim()) {
      setCallbackForms((prev) => ({
        ...prev,
        [messageId]: { ...form, error: "Name and phone are required." },
      }));
      return;
    }
    setCallbackForms((prev) => ({
      ...prev,
      [messageId]: { ...form, submitted: true, error: "" },
    }));
    setMessages((prev) => [
      ...prev,
      {
        id: "bot-callback-" + Date.now(),
        from: "bot",
        tag: "CALLBACK REQUESTED",
        text: "Thank you. Your callback request is noted. Our team will contact you shortly.",
      },
    ]);
    resetUnansweredFollowUp();
  };

  const sendToBot = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const userId = "user-" + Date.now();
    setMessages((prev) => [...prev, { id: userId, from: "user", text: trimmed }]);
    setTyping(true);

    const amcRenewalRequest = isAmcRenewalQuery(trimmed);
    const troubleshootingTree = getTroubleshootingTree(trimmed);
    const productSuggestion = getProductSuggestion(trimmed);
    const intent = classifyIntent(trimmed);
    timerRef.current = setTimeout(() => {
      setTyping(false);
      if (amcRenewalRequest) {
        resetUnansweredFollowUp();
        setTicketMode(false);
        setAmcMode(true);
        setMessages((prev) => [
          ...prev,
          {
            id: "bot-amc-" + Date.now(),
            from: "bot",
            tag: "AMC RENEWAL ASSISTANT",
            text: "Please share your AMC contract ID or registered email. I will check your contract status and expiry details.",
            quickReplies: ["Check AMC by contract ID", "Check AMC by email"],
          },
        ]);
        return;
      }
      if (troubleshootingTree) {
        resetUnansweredFollowUp();
        setAmcMode(false);
        const messageId = "bot-troubleshoot-" + Date.now();
        setTroubleshootStep(messageId, 0);
        setMessages((prev) => [
          ...prev,
          {
            id: messageId,
            from: "bot",
            tag: "GUIDED TROUBLESHOOTING",
            troubleshooting: troubleshootingTree,
            quickReplies: [],
          },
        ]);
        return;
      }
      if (productSuggestion) {
        resetUnansweredFollowUp();
        setAmcMode(false);
        setMessages((prev) => [
          ...prev,
          {
            id: "bot-product-" + Date.now(),
            from: "bot",
            tag: "PRODUCT ASSISTANT",
            productSuggestion,
            quickReplies: ["Compare more options", "Get exact quote", "Sale inquiry", "Raise a ticket"],
          },
        ]);
        return;
      }
      if (intent === "sales") {
        resetUnansweredFollowUp();
        setAmcMode(false);
        setTicketMode(false);
        const messageId = "bot-sale-form-" + Date.now();
        setMessages((prev) => [
          ...prev,
          {
            id: messageId,
            from: "bot",
            tag: "SALE INQUIRY",
            saleForm: true,
            text: "Share your details below. Our sales team will contact you shortly.",
            quickReplies: [],
          },
        ]);
        return;
      }
      if (intent === "ticket") {
        resetUnansweredFollowUp();
        setAmcMode(false);
        setTicketMode(false);
        const messageId = "bot-complaint-form-" + Date.now();
        setMessages((prev) => [
          ...prev,
          {
            id: messageId,
            from: "bot",
            tag: "COMPLAINT FORM",
            ticketForm: true,
            text: "Fill this complaint form to raise a new service desk ticket.",
            quickReplies: [],
          },
        ]);
        return;
      }
      const res = RESPONSES[intent] || RESPONSES.fallback;
      if (intent !== "amc") {
        setAmcMode(false);
      }
      setMessages((prev) => [
        ...prev,
        { id: "bot-" + Date.now(), from: "bot", tag: res.tag, text: res.text, quickReplies: res.quickReplies || [] },
      ]);
      if (intent === "fallback") {
        registerUnansweredFollowUp();
      } else {
        resetUnansweredFollowUp();
      }
    }, 900 + Math.floor(Math.random() * 300));
  };

  const lookupTicketInChat = async (ticketNumber) => {
    setTyping(true);
    try {
      const ticket = await trackTicket(ticketNumber);
      if (!ticket) {
        setMessages((prev) => [
          ...prev,
          {
            id: "bot-" + Date.now(),
            from: "bot",
            tag: "NOT FOUND",
            text: "No ticket found with that number. Please verify the ID and try again.",
            quickReplies: ["Check another ticket"],
          },
        ]);
        registerUnansweredFollowUp();
        return;
      }
      resetUnansweredFollowUp();
      setMessages((prev) => [
        ...prev,
        {
          id: "bot-" + Date.now(),
          from: "bot",
          tag: "TICKET STATUS",
          ticketLookup: {
            id: ticket.id,
            status: ticket.status,
            updatedAt: ticket.updatedAt,
            team: ticket.routedTeam,
            priority: ticket.effectivePriority,
            issueTitle: ticket.issueTitle,
            statusLog: ticket.statusLog || [],
          },
          quickReplies: ["Check another ticket"],
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: "bot-" + Date.now(),
          from: "bot",
          tag: "LOOKUP ERROR",
          text: err?.message || "Could not check ticket right now. Please try again in a moment.",
          quickReplies: ["Check another ticket"],
        },
      ]);
      registerUnansweredFollowUp();
    } finally {
      setTyping(false);
    }
  };

  const lookupAmcInChat = async (contractRef) => {
    setTyping(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 650));
      const contract = lookupAmcContract(contractRef);
      if (!contract) {
        setMessages((prev) => [
          ...prev,
          {
            id: "bot-amc-miss-" + Date.now(),
            from: "bot",
            tag: "CONTRACT NOT FOUND",
            text: "I could not find a matching AMC contract. Please verify contract ID/email or request live handoff below.",
            quickReplies: ["Check AMC again", "Talk to live agent"],
          },
        ]);
        registerUnansweredFollowUp();
        return;
      }
      resetUnansweredFollowUp();
      setMessages((prev) => [
        ...prev,
        {
          id: "bot-amc-status-" + Date.now(),
          from: "bot",
          tag: "AMC STATUS",
          amcStatus: {
            ...contract,
            crmNote: "CRM/database integration ready: replace mock lookup with live contract API.",
          },
          quickReplies: ["Renew this AMC", "Check another AMC"],
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  const onSubmit = async () => {
    if (!input.trim()) return;
    const value = input;
    setInput("");
    if (intakeMode === "server") {
      const userId = "user-" + Date.now();
      setMessages((prev) => [...prev, { id: userId, from: "user", text: value.trim() }]);
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            id: "bot-server-check-" + Date.now(),
            from: "bot",
            tag: "CHECKING",
            serverCheck: true,
            text:
              "Thanks. I am checking this with our server support checklist:\n" +
              "• Power/PSU and POST stage validation\n" +
              "• Hardware alert correlation (iDRAC/iLO)\n" +
              "• RAID and boot media status",
            quickReplies: ["Guided troubleshooting", "Raise a ticket"],
          },
        ]);
      }, 950);
      setIntakeMode(null);
      return;
    }
    if (intakeMode === "cloud") {
      const userId = "user-" + Date.now();
      setMessages((prev) => [...prev, { id: userId, from: "user", text: value.trim() }]);
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            id: "bot-cloud-check-" + Date.now(),
            from: "bot",
            tag: "CHECKING",
            text:
              "Great, I am checking best-fit cloud options for your requirement:\n" +
              "• Sizing and estimated monthly cost\n" +
              "• Recommended architecture (AWS/Azure/Hybrid)\n" +
              "• Migration approach and timeline",
            quickReplies: ["Get exact quote", "Raise a ticket"],
          },
        ]);
      }, 950);
      setIntakeMode(null);
      return;
    }
    if (ticketMode) {
      const userId = "user-" + Date.now();
      setMessages((prev) => [...prev, { id: userId, from: "user", text: value.trim() }]);
      await lookupTicketInChat(value.trim());
    } else if (amcMode) {
      const userId = "user-" + Date.now();
      setMessages((prev) => [...prev, { id: userId, from: "user", text: value.trim() }]);
      await lookupAmcInChat(value.trim());
    } else {
      sendToBot(value);
    }
    if (inputRef.current) {
      inputRef.current.style.height = "40px";
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const onInputResize = (e) => {
    e.currentTarget.style.height = "40px";
    e.currentTarget.style.height = Math.min(e.currentTarget.scrollHeight, 84) + "px";
  };

  const resetChat = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setTyping(false);
    setInput("");
    setTicketMode(false);
    setAmcMode(false);
    setUnansweredFollowUps(0);
    setCallbackForms({});
    setComplaintForms({});
    setTroubleshootProgress({});
    setIntakeMode(null);
    setMessages(buildGreetingState());
  };

  return (
    <>
      <button
        type="button"
        className={`et-chat-launcher ${open ? "is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close support chat" : "Open support chat"}
      >
        <i className={`bi ${open ? "bi-x-lg" : "bi-chat-dots-fill"}`} />
      </button>

      {open && (
        <section className="et-chat-window" aria-label="Emicron support chatbot">
          <header className="et-chat-header">
            <div className="et-chat-logo">ET</div>
            <div className="et-chat-header-info">
              <h3>Emicron Support AI</h3>
              <p>
                <span className="et-chat-dot" /> Online - Emicron Tech Solutions
              </p>
            </div>
            <div className="et-chat-header-actions">
              <button type="button" className="et-chat-icon-btn" title="New chat" onClick={resetChat}>
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button type="button" className="et-chat-icon-btn" title="Close" onClick={() => setOpen(false)}>
                <i className="bi bi-chevron-down" />
              </button>
            </div>
          </header>

          <div className="et-chat-cats">
            {categories.map((c) => (
              <button
                type="button"
                key={c.label}
                className={`et-chat-cat ${catActive === c.label ? "is-active" : ""}`}
                onClick={() => {
                  setCatActive(c.label);
                  setIntakeMode(null);
                  if (c.label === "Tickets") {
                    setAmcMode(false);
                    setTicketMode(false);
                    const messageId = "bot-complaint-form-" + Date.now();
                    setMessages((prev) => [
                      ...prev,
                      { id: "user-" + Date.now(), from: "user", text: c.prompt },
                      {
                        id: messageId,
                        from: "bot",
                        tag: "COMPLAINT FORM",
                        ticketForm: true,
                        text: "Please fill the complaint form below to raise your ticket.",
                        quickReplies: [],
                      },
                    ]);
                    return;
                  }
                  if (c.label === "Sale Inquiry") {
                    openSaleInquiryForm(c.prompt);
                    return;
                  }
                  if (c.label === "AMC") {
                    setTicketMode(false);
                    setAmcMode(true);
                    setMessages((prev) => [
                      ...prev,
                      { id: "user-" + Date.now(), from: "user", text: c.prompt },
                      {
                        id: "bot-" + Date.now(),
                        from: "bot",
                        tag: "AMC RENEWAL ASSISTANT",
                        text: "Share your AMC contract ID or registered email to check renewal status.",
                        quickReplies: ["Check AMC by contract ID", "Check AMC by email"],
                      },
                    ]);
                    return;
                  }
                  if (c.label === "Servers") {
                    setTicketMode(false);
                    setAmcMode(false);
                    setMessages((prev) => [...prev, { id: "user-" + Date.now(), from: "user", text: c.prompt }]);
                    startServerIntake();
                    return;
                  }
                  if (c.label === "Switches") {
                    setTicketMode(false);
                    setAmcMode(false);
                    setMessages((prev) => [...prev, { id: "user-" + Date.now(), from: "user", text: c.prompt }]);
                    postSwitchContactCard();
                    return;
                  }
                  if (c.label === "Cloud") {
                    setTicketMode(false);
                    setAmcMode(false);
                    setMessages((prev) => [...prev, { id: "user-" + Date.now(), from: "user", text: c.prompt }]);
                    startCloudIntake();
                    return;
                  }
                  setTicketMode(false);
                  setAmcMode(false);
                  sendToBot(c.prompt);
                }}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="et-chat-messages" ref={messagesRef}>
            {messages.map((m) => {
              if (m.type === "divider") {
                return (
                  <div key={m.id} className="et-chat-divider">
                    {m.text}
                  </div>
                );
              }

              return (
                <div key={m.id} className={`et-chat-row ${m.from === "user" ? "is-user" : ""}`}>
                  <div className={`et-chat-avatar ${m.from === "user" ? "is-user" : ""}`}>{m.from === "user" ? <i className="bi bi-person-fill" /> : "ET"}</div>
                  <div className={`et-chat-bubble ${m.from === "user" ? "is-user" : ""}`}>
                    {m.tag && <div className="et-chat-tag">{m.tag}</div>}
                    {m.isTicket ? (
                      <div>
                        <div className="et-chat-tag">SERVICE DESK</div>
                        <p>Your issue has been escalated to our Service Desk team.</p>
                        <div className="et-chat-ticket-card">
                          <div className="et-chat-ticket-id">{m.ticketId} - {nowTime()}</div>
                          <p>Priority: Medium - Assigned: Emicron Support Team</p>
                          <p>Expected response: Within 2-4 hours (business hours)</p>
                        </div>
                      </div>
                    ) : m.ticketForm ? (
                      <div>
                        <p>{m.text}</p>
                        {(() => {
                          const form = {
                            fullName: "",
                            email: "",
                            company: "",
                            phone: "",
                            issueType: "Other",
                            issueTitle: "",
                            issueDescription: "",
                            priority: "Medium",
                            submitting: false,
                            submitted: false,
                            ticketId: "",
                            error: "",
                            ...(complaintForms[m.id] || {}),
                          };
                          return (
                            <div className="et-chat-complaint-form">
                              <input className="et-chat-callback-input" placeholder="Full name *" value={form.fullName} onChange={(e) => updateComplaintField(m.id, "fullName", e.target.value)} />
                              <input className="et-chat-callback-input" type="email" placeholder="Email *" value={form.email} onChange={(e) => updateComplaintField(m.id, "email", e.target.value)} />
                              <input className="et-chat-callback-input" placeholder="Company *" value={form.company} onChange={(e) => updateComplaintField(m.id, "company", e.target.value)} />
                              <input className="et-chat-callback-input" placeholder="Phone" value={form.phone} onChange={(e) => updateComplaintField(m.id, "phone", e.target.value)} />
                              <div className="et-chat-complaint-row">
                                <select className="et-chat-callback-input" value={form.issueType} onChange={(e) => updateComplaintField(m.id, "issueType", e.target.value)}>
                                  <option>Network</option>
                                  <option>Cloud</option>
                                  <option>Firewall</option>
                                  <option>VPN</option>
                                  <option>Server</option>
                                  <option>Software</option>
                                  <option>Other</option>
                                </select>
                                <select className="et-chat-callback-input" value={form.priority} onChange={(e) => updateComplaintField(m.id, "priority", e.target.value)}>
                                  <option>Low</option>
                                  <option>Medium</option>
                                  <option>High</option>
                                  <option>Critical</option>
                                </select>
                              </div>
                              <input className="et-chat-callback-input" placeholder="Issue title *" value={form.issueTitle} onChange={(e) => updateComplaintField(m.id, "issueTitle", e.target.value)} />
                              <textarea className="et-chat-callback-input et-chat-complaint-textarea" rows={3} placeholder="Issue description *" value={form.issueDescription} onChange={(e) => updateComplaintField(m.id, "issueDescription", e.target.value)} />
                              {form.error && <div className="et-chat-callback-error">{form.error}</div>}
                              {form.submitted && form.ticketId && <div className="et-chat-complaint-success">Submitted - Ticket ID: {form.ticketId}</div>}
                              <div className="et-chat-trouble-actions">
                                <button type="button" className="et-chat-trouble-btn" onClick={() => { setTicketMode(true); setAmcMode(false); }}>
                                  Check existing ticket
                                </button>
                                <button type="button" className="et-chat-trouble-btn is-primary" onClick={() => submitComplaintFromChat(m.id)} disabled={form.submitting || form.submitted}>
                                  {form.submitting ? "Submitting..." : form.submitted ? "Submitted" : "Submit complaint"}
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ) : m.saleForm ? (
                      <div>
                        <p>{m.text}</p>
                        {(() => {
                          const form = {
                            name: "",
                            email: "",
                            company: "",
                            phone: "",
                            interest: "Servers",
                            message: "",
                            submitting: false,
                            submitted: false,
                            inquiryId: "",
                            error: "",
                            ...(saleForms[m.id] || {}),
                          };
                          return (
                            <div className="et-chat-complaint-form et-chat-sale-form">
                              <input className="et-chat-callback-input" placeholder="Full name *" value={form.name} onChange={(e) => updateSaleField(m.id, "name", e.target.value)} />
                              <input className="et-chat-callback-input" type="email" placeholder="Email *" value={form.email} onChange={(e) => updateSaleField(m.id, "email", e.target.value)} />
                              <input className="et-chat-callback-input" placeholder="Company name *" value={form.company} onChange={(e) => updateSaleField(m.id, "company", e.target.value)} />
                              <input className="et-chat-callback-input" placeholder="Phone" value={form.phone} onChange={(e) => updateSaleField(m.id, "phone", e.target.value)} />
                              <select className="et-chat-callback-input" value={form.interest} onChange={(e) => updateSaleField(m.id, "interest", e.target.value)}>
                                <option>Servers</option>
                                <option>Networking / Switches</option>
                                <option>Storage</option>
                                <option>Cloud</option>
                                <option>Security</option>
                                <option>AMC</option>
                                <option>Other</option>
                              </select>
                              <textarea
                                className="et-chat-callback-input et-chat-complaint-textarea"
                                rows={3}
                                placeholder="Requirement / message *"
                                value={form.message}
                                onChange={(e) => updateSaleField(m.id, "message", e.target.value)}
                              />
                              {form.error && <div className="et-chat-callback-error">{form.error}</div>}
                              {form.submitted && (
                                <div className="et-chat-complaint-success">
                                  Sale inquiry submitted{form.inquiryId ? ` (${form.inquiryId})` : ""}.
                                </div>
                              )}
                              <div className="et-chat-trouble-actions">
                                <button
                                  type="button"
                                  className="et-chat-trouble-btn is-primary"
                                  onClick={() => submitSaleFromChat(m.id)}
                                  disabled={form.submitting || form.submitted}
                                >
                                  {form.submitting ? "Submitting..." : form.submitted ? "Submitted" : "Submit sale inquiry"}
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ) : m.ticketLookup ? (
                      <div>
                        <p><strong>Ticket:</strong> {m.ticketLookup.id}</p>
                        <p><strong>Status:</strong> {formatStatusLabel(m.ticketLookup.status)}</p>
                        <p><strong>Team:</strong> {m.ticketLookup.team || "Support"}</p>
                        <p><strong>Priority:</strong> {m.ticketLookup.priority || "Normal"}</p>
                        <p><strong>Issue:</strong> {m.ticketLookup.issueTitle || "-"}</p>
                        <div className="et-chat-ticket-card">
                          <div className="et-chat-ticket-id">
                            Last updated: {m.ticketLookup.updatedAt ? new Date(m.ticketLookup.updatedAt).toLocaleString() : nowTime()}
                          </div>
                          {(m.ticketLookup.statusLog || []).slice(-3).reverse().map((log, idx) => (
                            <p key={idx}>
                              {new Date(log.at).toLocaleString()} - {formatStatusLabel(log.status)}{log.note ? ` - ${log.note}` : ""}
                            </p>
                          ))}
                        </div>
                      </div>
                    ) : m.productSuggestion ? (
                      <div>
                        <p>
                          {m.productSuggestion.overBudget
                            ? "No exact options found within your budget. Closest matching models are:"
                            : `Here are suitable ${m.productSuggestion.category} models${m.productSuggestion.budgetRupees ? ` under ${formatInr(m.productSuggestion.budgetRupees)}` : ""}:`}
                        </p>
                        {m.productSuggestion.formFactor && (
                          <p><strong>Form factor:</strong> {m.productSuggestion.formFactor}</p>
                        )}
                        <div className="et-chat-product-card">
                          {m.productSuggestion.items.map((item, idx) => (
                            <div key={idx} className="et-chat-product-row">
                              <div className="et-chat-product-model">{item.model}</div>
                              <div className="et-chat-product-specs">
                                CPU: {item.cpu} | RAM: {item.ram}
                              </div>
                              <div className="et-chat-product-specs">Storage: {item.storage}</div>
                              <div className="et-chat-product-price">{formatInr(item.price)}</div>
                            </div>
                          ))}
                        </div>
                        <p className="et-chat-product-note">For final quote, warranty terms, and delivery timeline, ask for "exact quote".</p>
                      </div>
                    ) : m.switchHelp ? (
                      <div>
                        <p>{m.text}</p>
                        <div className="et-chat-switch-card">
                          <a href={`tel:${CONTACT_PHONE_TEL}`} className="et-chat-escalation-link">
                            <i className="bi bi-telephone-fill" /> Call: {CONTACT_PHONE}
                          </a>
                          <a href="mailto:info@emicrontech.com?subject=Switch%20Support%20Required" className="et-chat-escalation-link">
                            <i className="bi bi-envelope-fill" /> Email: info@emicrontech.com
                          </a>
                          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="et-chat-escalation-link">
                            <i className="bi bi-whatsapp" /> WhatsApp live support
                          </a>
                        </div>
                      </div>
                    ) : m.amcStatus ? (
                      <div>
                        <p><strong>Customer:</strong> {m.amcStatus.customer}</p>
                        <p><strong>Plan:</strong> {m.amcStatus.plan}</p>
                        <div className="et-chat-amc-card">
                          <div className="et-chat-amc-row"><span>Status</span><strong>{m.amcStatus.status}</strong></div>
                          <div className="et-chat-amc-row"><span>Start</span><strong>{new Date(m.amcStatus.startDate).toLocaleDateString()}</strong></div>
                          <div className="et-chat-amc-row"><span>Expiry</span><strong>{new Date(m.amcStatus.expiryDate).toLocaleDateString()}</strong></div>
                          <div className="et-chat-amc-row"><span>SLA</span><strong>{m.amcStatus.sla}</strong></div>
                          <div className="et-chat-amc-row"><span>Covered Assets</span><strong>{m.amcStatus.assets}</strong></div>
                        </div>
                        <p className="et-chat-product-note">{m.amcStatus.crmNote}</p>
                      </div>
                    ) : m.troubleshooting ? (
                      <div>
                        <p>
                          <strong>{m.troubleshooting.title}</strong> - {m.troubleshooting.category}
                        </p>
                        <p>Follow this guided flow before raising a ticket:</p>
                        {(() => {
                          const idx = typeof troubleshootProgress[m.id] === "number" ? troubleshootProgress[m.id] : 0;
                          const total = m.troubleshooting.steps.length;
                          const isLast = idx >= total - 1;
                          return (
                            <div className="et-chat-trouble-card">
                              <div className="et-chat-trouble-step-head">Step {Math.min(idx + 1, total)} of {total}</div>
                              <div className="et-chat-trouble-step-body">{m.troubleshooting.steps[idx]}</div>
                              <div className="et-chat-trouble-actions">
                                <button
                                  type="button"
                                  className="et-chat-trouble-btn"
                                  onClick={() => setTroubleshootStep(m.id, Math.max(idx - 1, 0))}
                                  disabled={idx === 0}
                                >
                                  Previous
                                </button>
                                {!isLast ? (
                                  <button
                                    type="button"
                                    className="et-chat-trouble-btn is-primary"
                                    onClick={() => setTroubleshootStep(m.id, Math.min(idx + 1, total - 1))}
                                  >
                                    Next step
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="et-chat-trouble-btn is-primary"
                                    onClick={() => {
                                      setTicketMode(true);
                                      setMessages((prev) => [
                                        ...prev,
                                        {
                                          id: "bot-" + Date.now(),
                                          from: "bot",
                                          tag: "RAISE TICKET",
                                          text: "If issue still persists, share your ticket number to track status or raise a new issue via Service Desk.",
                                          quickReplies: ["Raise a ticket", "Check ticket status"],
                                        },
                                      ]);
                                    }}
                                  >
                                    Escalate to ticket
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ) : m.escalation ? (
                      <div>
                        <p>{m.text}</p>
                        <div className="et-chat-escalation-actions">
                          <a
                            className="et-chat-escalation-link"
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="bi bi-whatsapp" /> WhatsApp Live Agent
                          </a>
                          <a className="et-chat-escalation-link" href="mailto:info@emicrontech.com?subject=Live%20Support%20Handoff">
                            <i className="bi bi-envelope-fill" /> Email Support
                          </a>
                        </div>
                        {(() => {
                          const form = {
                            name: "",
                            phone: "",
                            time: "",
                            submitted: false,
                            error: "",
                            ...(callbackForms[m.id] || {}),
                          };
                          return (
                            <div className="et-chat-callback-form">
                              <div className="et-chat-callback-title">Request a callback</div>
                              <input
                                className="et-chat-callback-input"
                                placeholder="Your name"
                                value={form.name}
                                onChange={(e) => updateCallbackField(m.id, "name", e.target.value)}
                              />
                              <input
                                className="et-chat-callback-input"
                                placeholder="Phone number"
                                value={form.phone}
                                onChange={(e) => updateCallbackField(m.id, "phone", e.target.value)}
                              />
                              <input
                                className="et-chat-callback-input"
                                placeholder="Preferred time (optional)"
                                value={form.time}
                                onChange={(e) => updateCallbackField(m.id, "time", e.target.value)}
                              />
                              {form.error && <div className="et-chat-callback-error">{form.error}</div>}
                              <button
                                type="button"
                                className="et-chat-callback-btn"
                                onClick={() => submitCallbackRequest(m.id)}
                                disabled={form.submitted}
                              >
                                {form.submitted ? "Requested" : "Submit callback request"}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <p>{m.text}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {messages
              .filter((m) => m.from === "bot" && m.quickReplies?.length)
              .slice(-1)
              .map((m) => (
                <div key={m.id + "-qr"} className="et-chat-quick-row">
                  {m.quickReplies.map((q) => (
                    <button type="button" key={q} className="et-chat-quick-btn" onClick={() => handleQuickReplyAction(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              ))}

            {typing && (
              <div className="et-chat-row">
                <div className="et-chat-avatar">ET</div>
                <div className="et-chat-bubble">
                  <div className="et-chat-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="et-chat-input-wrap">
            <textarea
              ref={inputRef}
              className="et-chat-input"
              rows={1}
              value={input}
              placeholder={
                ticketMode
                  ? "Enter ticket number (EMI-YYYYMMDD-XXXXX)"
                  : amcMode
                    ? "Enter AMC contract ID or registered email"
                    : intakeMode === "server"
                      ? "Paste server issue details as requested above..."
                      : intakeMode === "cloud"
                        ? "Share your cloud requirement details..."
                    : "Ask about servers, switches, cloud, support..."
              }
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onInput={onInputResize}
            />
            <button type="button" className="et-chat-send" onClick={onSubmit} aria-label="Send message">
              <i className="bi bi-send-fill" />
            </button>
          </div>
        </section>
      )}
    </>
  );
}
